// Product analytics — PostHog, wrapped so the rest of the app can't misuse it.
//
// Privacy posture (locked with Nick 2026-06-09):
//   - Analytics NEVER carries GPS coordinates, checkpoint tokens/names, emails, or free text.
//     The event allowlist below is the entire vocabulary; unknown events don't compile, unknown
//     props are stripped at runtime. Identity is the opaque Supabase user UUID + account tail.
//   - autocapture, pageview capture, session recording, and the remote /decide config are all OFF
//     at init. Feature gating stays first-party (see src/flags) — PostHog is ingest-only here.
//   - The SDK loads lazily and ONLY when a runtime-injected phc_ key exists AND the web_analytics
//     flag is on. No key (or flag off) = a frozen no-op — zero network, zero bundle cost.

export type AnalyticsEventName =
  | "app_booted"
  | "capture_completed"
  | "field_status_opened"
  | "timeclock_punch"
  | "web_access_blocked";

/** Per-event prop allowlist — anything else is stripped before it can leave the device. */
// Property names follow the shared mobile contract (apps/mobile mobile-analytics, 6b382ed):
// camelCase, appVersion on every business event, outcome on completion when known.
const EVENT_PROPS: Record<AnalyticsEventName, readonly string[]> = {
  app_booted: ["appVersion", "formFactor", "standalone", "hasCamera", "hasWebNfc", "networkOnline"],
  capture_completed: ["appVersion", "method", "offline", "queued", "outcome"],
  field_status_opened: [],
  timeclock_punch: ["appVersion", "punchType"],
  web_access_blocked: [],
};

/** Structural slice of posthog-js we call — injectable for tests. */
export interface PosthogLike {
  init(key: string, options: Record<string, unknown>): void;
  capture(event: string, props?: Record<string, unknown>): void;
  identify(id: string, props?: Record<string, unknown>): void;
  group?(type: string, key: string): void;
}

export interface Analytics {
  readonly enabled: boolean;
  capture(event: AnalyticsEventName, props?: Record<string, string | number | boolean>): void;
  /** Identify with the opaque Supabase user UUID; accountTail is the last-4 account id chars. */
  identify(userId: string, accountTail: string): void;
}

export const NOOP_ANALYTICS: Analytics = Object.freeze({
  enabled: false,
  capture() {},
  identify() {},
});

async function defaultLoader(): Promise<PosthogLike> {
  const mod = await import("posthog-js");
  return mod.default as unknown as PosthogLike;
}

export function createAnalytics(options: {
  key: string | null;
  host: string | null;
  enabled: boolean;
  /** Which product surface these events come from — one PostHog project serves all surfaces. */
  surface: "guard-web" | "console";
  loader?: () => Promise<PosthogLike>;
}): Analytics {
  const { key, host, enabled, surface } = options;
  if (!enabled || key === null) {
    return NOOP_ANALYTICS;
  }
  const loader = options.loader ?? defaultLoader;

  let client: PosthogLike | null = null;
  let loadStarted = false;
  const pending: Array<(ph: PosthogLike) => void> = [];

  function run(action: (ph: PosthogLike) => void): void {
    if (client !== null) {
      action(client);
      return;
    }
    pending.push(action);
    if (loadStarted) {
      return;
    }
    loadStarted = true;
    void loader()
      .then((ph) => {
        ph.init(key as string, {
          api_host: host ?? "https://us.i.posthog.com",
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          disable_session_recording: true,
          advanced_disable_decide: true,
          persistence: "localStorage",
          person_profiles: "identified_only",
        });
        client = ph;
        for (const action of pending.splice(0)) {
          action(ph);
        }
      })
      .catch(() => {
        // SDK failed to load (offline, blocked) — analytics silently stays off this session.
        pending.length = 0;
      });
  }

  return {
    enabled: true,
    capture(event, props) {
      const allowed = EVENT_PROPS[event];
      const safe: Record<string, string | number | boolean> = { surface };
      for (const prop of allowed) {
        const value = props?.[prop];
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
          safe[prop] = value;
        }
      }
      run((ph) => ph.capture(event, safe));
    },
    identify(userId, accountTail) {
      run((ph) => {
        ph.identify(userId, { account_tail: accountTail });
        ph.group?.("account", accountTail);
      });
    },
  };
}
